import { useEffect, useRef, useState } from 'react';
import { cleanClass } from '@shared/utils/cleanClass.js';
import PageTitle from './PageTitle.jsx';
import PageDescription from './PageDescription.jsx';
import PageTableFloatingMenu from './PageTableFloatingMenu.jsx';
import PageHeader from './PageHeader.jsx';

const PageWrapperWithNav = ({ children = null, header = null, title = null, returnLabel, returnRoute, description = null, nav = null, filters = null, asideWidth = '200px', name = '', useFloatingMenu = false, row = null, floatingMenuActions = [], className }) => {
  const [positionTop, setPositionTop] = useState(0);
  const [positionLeft, setPositionLeft] = useState(0);
  const [positionRight, setPositionRight] = useState(0);

  const wrapperRef = useRef(null);
  const contentRef = useRef(null);
  const filtersRef = useRef(null);
  const navRef = useRef(null);

  const updatePositions = () => {
    if (contentRef.current && wrapperRef.current) {
      const contentTop = contentRef.current.getBoundingClientRect().top;
      const wrapperTop = wrapperRef.current.getBoundingClientRect().top;
      const distance = contentTop - wrapperTop;
      setPositionTop(distance);
    }
    if (filtersRef.current) {
      const width = filtersRef.current.offsetWidth;
      setPositionRight(-width - 16);
    }
    if (navRef.current) {
      const width = navRef.current.offsetWidth;
      setPositionLeft(-width - 8);
    }
  };

  useEffect(() => {
    updatePositions();

    const resizeObserver = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        updatePositions();
      });
    });

    if (filtersRef.current) {
      resizeObserver.observe(filtersRef.current);
    }

    window.addEventListener('resize', updatePositions);

    return () => {
      if (filtersRef.current) {
        resizeObserver.unobserve(filtersRef.current);
      }
      resizeObserver.disconnect();

      window.removeEventListener('resize', updatePositions);
    };
  }, []);

  useEffect(() => {
    updatePositions();
  }, [nav, filters]);

  return (
    <div className={cleanClass(`comp-page-wrapper with-nav ${name} ${className}`)} ref={wrapperRef}>
      {nav && (
        <div className="nav" ref={navRef} style={{ minWidth: asideWidth, top: '55px', left: positionLeft }}>
          {nav}
        </div>
      )}
      <div className="main-column">
        {header}
        {!header && title && (
          <>
            <PageHeader>
              <PageTitle returnLabel={returnLabel} returnRoute={returnRoute}>
                {title}
              </PageTitle>
              {description && <PageDescription>{description}</PageDescription>}
            </PageHeader>
          </>
        )}
        <div className="content" ref={contentRef}>
          {children}
        </div>
      </div>
      {filters && (
        <div className="filters" ref={filtersRef} style={{ minWidth: asideWidth, top: positionTop, right: positionRight }}>
          {filters}
        </div>
      )}
      {useFloatingMenu && <PageTableFloatingMenu parentRef={contentRef} rowClass={row} actions={floatingMenuActions} />}
    </div>
  );
};

export default PageWrapperWithNav;
